/**
 * ReportAuthContext — Public report viewers can unlock participant-only
 * content with a per-instance PIN code (`settings.reportPin`). The PIN never
 * reaches the browser: the instance carries only `settings.hasReportPin`, and
 * signIn() asks the backend (API_ENDPOINTS.INSTANCE_REPORT_PIN) — #836.
 *
 * Two states:
 *   - `pinRequired = false` → no gate, every viewer sees everything.
 *   - `pinRequired = true`  → public viewers see public content; the
 *                              participant-only blocks render a teaser
 *                              + "Kirjaudu" button until they unlock.
 *
 * Unlock state is the server's (#1042): a correct PIN opens a report session
 * (the kuutio_report_session cookie), and the API serves PIN-only content only
 * to its holders. A sign-in unlocks too when it carries a grant on this
 * instance or system role facilitator or higher (#1475) — not a sign-in alone.
 * On mount the provider asks the server for both (`unlocked`, `account`).
 * localStorage `kuutio-report-auth-<instanceId>` mirrors the combined answer,
 * because PublicWorkshopReport reads it synchronously while rendering.
 *
 * The chat follows the same answer whether or not the report has a PIN: the
 * server serves it only to a viewer the report unlocks for (#1408), so an
 * anonymous reader of a public report gets no chat tab.
 *
 * Sister docs:
 *   - SignInButton renders the top-bar affordance.
 *   - SignInPinModal handles PIN entry.
 *   - GateBlock wraps content that should be hidden until authed.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';

const Ctx = createContext({
  pinRequired: false,
  isAuthed: true,        // when no PIN is set, treat as authed
  signIn: async () => false,
  signOut: () => {},
});

const storageKey = (instanceId) => `kuutio-report-auth-${instanceId}`;

/** Keep the localStorage mirror in step with the server's answer. */
function mirror(instanceId, unlocked) {
  try {
    if (unlocked) localStorage.setItem(storageKey(instanceId), '1');
    else localStorage.removeItem(storageKey(instanceId));
  } catch {
    // Private browsing / disabled storage — the in-memory state still applies.
  }
}

export function ReportAuthProvider({ instanceId, pinRequired = false, children }) {
  // `authed`: this browser holds a report-PIN session. `accountAuthed`: the
  // viewer's sign-in alone opens the report, as the server decides it.
  const [authed, setAuthed] = useState(false);
  const [accountAuthed, setAccountAuthed] = useState(false);
  // Bumped by each server answer, so the lock state is broadcast even when it
  // did not change: the report re-reads the localStorage mirror on that event.
  const [checks, setChecks] = useState(0);

  // Ask the server on mount — also re-runs if the instance changes (e.g. the
  // user switches workshops in the same tab). A localStorage flag left from
  // before #1042 without a session is cleared: the PIN is asked again.
  useEffect(() => {
    if (!instanceId) { setAuthed(false); setAccountAuthed(false); return undefined; }
    let cancelled = false;
    fetch(API_ENDPOINTS.INSTANCE_REPORT_SESSION(instanceId), { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : {}))
      .catch(() => ({}))
      .then(({ unlocked, account }) => {
        if (cancelled) return;
        const byAccount = !!account && account !== 'public';
        if (pinRequired) mirror(instanceId, !!unlocked || byAccount);
        setAuthed(!!unlocked);
        setAccountAuthed(byAccount);
        setChecks((c) => c + 1);
      });
    return () => { cancelled = true; };
  }, [instanceId, pinRequired]);

  const signIn = useCallback(async (attempt) => {
    if (!pinRequired) return true;
    let ok = false;
    try {
      const res = await fetch(API_ENDPOINTS.INSTANCE_REPORT_PIN(instanceId), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: String(attempt || '').trim() }),
      });
      ok = res.ok && (await res.json()).ok === true;
    } catch {
      ok = false;
    }
    if (!ok) return false;
    mirror(instanceId, true);
    setAuthed(true);
    return true;
  }, [instanceId, pinRequired]);

  const signOut = useCallback(() => {
    fetch(API_ENDPOINTS.INSTANCE_REPORT_SESSION(instanceId), { method: 'DELETE', credentials: 'include' }).catch(() => {});
    mirror(instanceId, accountAuthed);
    setAuthed(false);
  }, [instanceId, accountAuthed]);

  const value = useMemo(() => ({
    pinRequired,
    isAuthed: !pinRequired || authed || accountAuthed,
    accountAuthed,
    signIn,
    signOut,
  }), [pinRequired, authed, accountAuthed, signIn, signOut]);

  // Broadcast the chat's lock state to anything outside the report tree that
  // wants to react — primarily App.jsx, which unregisters the chat drawer tab
  // while the viewer is locked (a PIN report not unlocked, or any report the
  // viewer has no session or grant for, #1408). Custom event over context so
  // it crosses route boundaries cleanly (the provider lives inside the
  // route; App.jsx is above it). Unmount resets to `false` so other
  // routes never inherit a stale lock.
  useEffect(() => {
    const locked = !authed && !accountAuthed;
    window.dispatchEvent(new CustomEvent('kuutio:report-chat-locked', { detail: { locked } }));
    return () => {
      window.dispatchEvent(new CustomEvent('kuutio:report-chat-locked', { detail: { locked: false } }));
    };
  }, [pinRequired, authed, accountAuthed, checks]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useReportAuth() {
  return useContext(Ctx);
}
